import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { graphql, useStaticQuery } from 'gatsby';
import { mediaQuery } from '../utils-css/media-query';
import { ExternalLinkHighlight, highlightStyle, InternalLinkHighlight } from '../atoms/link-highlight';
import { blogMenu, MenuItem, techMenu, whoWeAreMenu } from '../../../types/menu';
import { Separator } from '../atoms/separator';
import { Dropdown } from './dropdown';

interface ShouldOpenMenuProp {
  $openMenu: boolean;
}

const MenuItems = styled.nav<ShouldOpenMenuProp>`
  display: flex;
  margin: 0;
  flex-direction: column;
  width: 90%;

  ${mediaQuery.minWidth.lg} {
    flex-direction: row;
    visibility: visible;
    opacity: 1;
    margin-left: auto;
    width: auto;
  }
`;

const menuItemStyle = css`
  display: inline-block;
  white-space: nowrap;

  ${({ theme }): string => theme.mixins.padding({ hor: 0, ver: 0 })};
  ${({ theme }): string => theme.mixins.margin({ hor: 2, ver: 1 })};
`;

const ExternalMenuItem = styled(ExternalLinkHighlight)`
  ${menuItemStyle};

  display: flex;
  align-items: center;
  flex-direction: column;
`;

type InternalMenuItemProps = ShouldOpenMenuProp & {
  $isSelected: boolean;
};

const InternalMenuItem = styled(InternalLinkHighlight)<InternalMenuItemProps>`
  ${menuItemStyle};

  ${mediaQuery.minWidth.lg} {
    display: block;
  }

  ${(props) => props.$isSelected && highlightStyle}
`;

type MenuProps = ShouldOpenMenuProp & {
  pathname?: string;
  data: {
    siteMetadata: {
      title: string;
      careers: string;
      meetup: string;
      planYourJourney: string;
      companyUrl: string;
    };
  };
};

const MenuSeparator = styled(Separator)`
  ${({ theme }): string => theme.mixins.margin({ bottom: 0 })};

  ${mediaQuery.minWidth.lg} {
    display: none;
  }
  width: 100%;
`;

const MenuEntry: FC<{ menuItem: MenuItem; $openMenu: boolean; pathname?: string }> = ({
  menuItem,
  $openMenu,
  pathname,
}) =>
  menuItem.internal ? (
    <InternalMenuItem
      key={menuItem.path}
      $openMenu={$openMenu}
      to={menuItem.path}
      target="_blank"
      $isSelected={pathname?.includes(menuItem.path) ?? false}
    >
      {menuItem.label}
    </InternalMenuItem>
  ) : (
    <ExternalMenuItem key={menuItem.path} href={menuItem.path} target="_blank">
      {menuItem.label}
    </ExternalMenuItem>
  );

export const Menu = React.memo<MenuProps>(({ pathname, data, $openMenu }) => {
  const techRadar = useStaticQuery<Queries.TechRadarMenuQuery>(graphql`
    query TechRadarMenu {
      allSitePage(filter: { path: { regex: "/tech-radar/" } }, sort: { path: ASC }, limit: 1) {
        nodes {
          path
        }
      }
    }
  `);

  return (
    <MenuItems $openMenu={$openMenu}>
      <MenuSeparator key="top" />
      <Dropdown label="Our tech ecosystem" key="tech-overview">
        {techMenu(techRadar.allSitePage.nodes[0].path).map((techMenuItem) => (
          <MenuEntry key={techMenuItem.path} menuItem={techMenuItem} $openMenu={$openMenu} pathname={pathname} />
        ))}
      </Dropdown>
      <Dropdown label="Who we are" key="who-we-are">
        {whoWeAreMenu(data.siteMetadata.careers, data.siteMetadata.planYourJourney, data.siteMetadata.companyUrl).map(
          (whoWeAreMenuItem) => (
            <MenuEntry
              key={whoWeAreMenuItem.path}
              menuItem={whoWeAreMenuItem}
              $openMenu={$openMenu}
              pathname={pathname}
            />
          )
        )}
      </Dropdown>
      {blogMenu().map((blogMenuItem) => (
        <MenuEntry key={blogMenuItem.path} menuItem={blogMenuItem} $openMenu={$openMenu} pathname={pathname} />
      ))}
      <MenuSeparator />
    </MenuItems>
  );
});
